<template>
  <div class="kiosk-table-picker">
    <!-- Full-screen loader while the first session's customizations preload,
         so the picker only ever paints with the restaurant's branding. -->
    <div
      v-if="preloading"
      class="splash-loader-screen"
      data-cy="kiosk-branding-loader"
    >
      <v-progress-circular
        indeterminate
        :size="48"
        :width="4"
        color="grey darken-1"
      />
    </div>
    <template v-else>
      <!-- No :cover — the picker keeps the plain header shape and colors it
           with the restaurant's customizations instead. -->
      <SplashHeader :fill="splashBackgroundColor" />
    <div class="kiosk-table-picker__top">
      <v-btn depressed rounded class="kiosk-table-picker__logo">
        <GreetLogo :width="65" color="black" />
      </v-btn>
      <v-btn
        v-if="languageList.length > 1"
        fab
        small
        icon
        class="kiosk-table-picker__language"
        data-cy="kiosk-language-selector"
        @click="openLanguageSelector"
      >
        <img :src="languageImage" style="width: 22px" />
      </v-btn>
    </div>
    <div
      class="d-flex flex-column justify-center align-center text-center px-4 flex-grow-1"
    >
      <Avatar
        v-if="restaurantLogo"
        :photo-url="restaurantLogo"
        :lazy-src="restaurantLogo"
        data-cy="kiosk-restaurant-logo"
        :size="140"
      />
      <h2
        v-if="restaurantName"
        class="mt-4 tp-title-medium"
        data-cy="kiosk-restaurant-name"
      >
        {{ restaurantName }}
      </h2>
      <!-- <p class="mt-2 mb-0 tp-text-body">
        {{ $t('screens.kioskTablePicker.title') }}
      </p> -->
      <template v-if="qrCodes.length === 0">
        <p class="mt-6 mb-0 tp-text-body" data-cy="kiosk-picker-empty">
          {{ $t('screens.kioskTablePicker.emptyStateTitle') }}
        </p>
        <p class="mt-2 mb-0 tp-text-caption-dark">
          {{ $t('screens.kioskTablePicker.emptyStateSubtitle') }}
        </p>
      </template>
      <div v-else class="w-100 mt-3">
        <p
          v-if="failedSession"
          class="mb-0 tp-text-caption-dark"
          data-cy="kiosk-picker-error"
        >
          {{ $t('screens.kioskTablePicker.sessionErrorSubtitle') }}
        </p>
        <Button
          v-for="(qrCode, index) in qrCodes"
          :key="`${qrCode.session}-${index}`"
          block
          gray
          class="mt-4 justify-space-between"
          :disabled="
            loadingSession !== null && loadingSession !== qrCode.session
          "
          :data-cy="`kiosk-table-${index}`"
          @click="() => selectQrCode(qrCode)"
        >
          <div
            class="grey darken-4 d-flex align-center rounded-lg justify-center kiosk-table-picker__badge"
          >
            {{ index + 1 }}
          </div>
          <div class="text-left w-100 ml-4">
            {{ qrCode.label || defaultLabel(index) }}
          </div>
          <v-progress-circular
            v-if="loadingSession === qrCode.session"
            indeterminate
            :size="15"
            :width="2"
            class="mr-2"
          />
          <v-icon v-else right color="grey darken-4">
            mdi-chevron-right
          </v-icon>
        </Button>
      </div>
    </div>
      <LocaleSelector
        ref="languageSelector"
        :language-list="languageList"
        :selected-language="$i18n.locale()"
        @changeLanguage="changeLanguage"
      />
    </template>
  </div>
</template>

<script>
import Avatar from '@/components/common/Avatar.vue';
import Button from '@/components/Button.vue';
import GreetLogo from '@/components/GreetLogo.vue';
import LocaleSelector from '@/components/AgnosticLocaleSelector.vue';
import SplashHeader from '@/components/SplashScreen/Header.vue';
import { parseQueryParam } from '@/utils/strings';

// Only the languages the kiosk currently ships with — extend as it adds more.
const SUPPORTED_LANGUAGES = ['en', 'lt'];

export default {
  name: 'KioskTablePicker',
  components: {
    Avatar,
    Button,
    GreetLogo,
    LocaleSelector,
    SplashHeader,
  },
  metaInfo() {
    return {
      title: this.restaurantName
        ? `${this.restaurantName} - Greet.menu`
        : 'Greet.menu',
    };
  },
  data() {
    return {
      loadingSession: null,
      failedSession: null,
      // True while the first session preloads in the background — shows the
      // mini loader standing in for the restaurant branding.
      preloading: false,
      preloadedSession: null,
      preloadPromise: null,
      // What the kiosk asked for, or what the guest picked afterwards.
      languageCode: null,
    };
  },
  computed: {
    loadingScreenCustomizations() {
      return this.$store.getters.loadingScreenCustomizations;
    },
    // Same sources as SplashScreenV2, but without the generic merchant.svg
    // fallback — until the first session preloads there is no restaurant,
    // and flashing the placeholder icon would look broken.
    restaurantLogo() {
      return (
        this.$store.getters.restaurantLogo ||
        this.loadingScreenCustomizations.logo ||
        null
      );
    },
    splashBackgroundColor() {
      // Most restaurants only set primary-color, so fall back to it — without
      // this the header keeps the hardcoded tipsColorGlobal red.
      return (
        this.loadingScreenCustomizations.background_color ||
        this.$store.getters.customizationPrimaryColor
      );
    },
    restaurantName() {
      return parseQueryParam(this.$route.query.restaurantName) || '';
    },
    qrCodes() {
      try {
        const parsed = JSON.parse(
          parseQueryParam(this.$route.query.qrCodes) || '[]'
        );
        if (!Array.isArray(parsed)) {
          return [];
        }

        // The kiosk creates the sessions, so an entry without one is unusable
        // here — this screen never creates a session of its own.
        return parsed
          .filter(
            qrCode =>
              qrCode &&
              typeof qrCode.session === 'string' &&
              qrCode.session.trim().length > 0
          )
          .map(qrCode => ({
            session: qrCode.session.trim(),
            label: typeof qrCode.label === 'string' ? qrCode.label.trim() : '',
          }));
      } catch (e) {
        return [];
      }
    },
    // Before a table is picked there is no restaurant to read languages from,
    // so the kiosk passes them in the URL. Falls back to all supported ones.
    languageList() {
      const fromQuery = (parseQueryParam(this.$route.query.languages) || '')
        .split(',')
        .map(code => code.trim().toLowerCase())
        .filter(code => SUPPORTED_LANGUAGES.includes(code));

      return fromQuery.length > 0
        ? [...new Set(fromQuery)]
        : SUPPORTED_LANGUAGES;
    },
    // Which language the kiosk wants the screen to start in.
    initialLanguage() {
      const code = (parseQueryParam(this.$route.query.lang) || '')
        .trim()
        .toLowerCase();

      return this.languageList.includes(code) ? code : null;
    },
    languageImage() {
      const locale = this.$i18n.locale();

      try {
        return require(`@/assets/images/flags/${locale}.svg`);
      } catch (e) {
        return require('@/assets/images/flags/en.svg');
      }
    },
  },
  mounted() {
    window.scrollTo({ top: 0 });

    // Lets the rest of the app send the kiosk back to this picker
    // instead of recycling the single-QR session.
    this.$store.dispatch('setKioskMultiModeUrl', window.location.href);

    if (this.qrCodes.length === 1) {
      this.selectQrCode(this.qrCodes[0]);
    } else if (this.qrCodes.length > 1) {
      // Most guests take the first table, so load it while they are still
      // reading the list — that tap then only has to change the route.
      this.preloadFirstSession();

      if (this.initialLanguage) {
        this.changeLanguage({ code: this.initialLanguage });
      }
    }
  },
  methods: {
    openLanguageSelector() {
      this.$refs.languageSelector.open();
    },
    async changeLanguage(language) {
      // Stored first: the session load running in the background re-applies
      // whatever selectedLanguage holds once it knows the restaurant, so it
      // has to see this choice rather than the one it started with.
      this.languageCode = language.code;
      this.$store.dispatch('setSelectedLanguage', language.code);

      const translations = (await import(`../i18n/${language.code}.json`))
        .default;

      this.$i18n.add(language.code, translations);
      this.$i18n.set(language.code);

      // No-op until a restaurant is loaded; once a session is loaded this pulls
      // the menu translations so the picked table opens in this language.
      this.$store.dispatch('fetchTranslationsForLanguage', language.code);
    },
    defaultLabel(index) {
      return this.$t('screens.kioskTablePicker.defaultQrCodeLabel', {
        number: index + 1,
      });
    },
    preloadFirstSession() {
      const [firstQrCode] = this.qrCodes;

      this.$store.dispatch('deleteSessionCookie');
      this.preloading = true;
      this.preloadPromise = this.loadSession(firstQrCode.session)
        .then(() => {
          this.preloadedSession = firstQrCode.session;
        })
        .catch(e => {
          // Nothing to show yet — the guest has not picked anything. Selecting
          // this table later retries and surfaces the error then.
          console.error(e);
        })
        .finally(() => {
          this.preloading = false;
        });
    },
    async loadSession(session) {
      // fetchSessionData is a no-op while a restaurant is already loaded, so
      // without this reset a second pick would keep the previously picked
      // table's restaurant, menu and table data. Same reset the food court
      // list does when switching restaurants.
      this.$store.dispatch('resetRestaurantId');

      // force skips the same-device check, which a kiosk-created session
      // would not pass, and re-fetches the menu — otherwise the second pick
      // keeps the first table's already-loaded categories.
      await this.$store.dispatch('fetchSessionData', { session, force: true });

      // Loading a session switches the locale to the restaurant's own language,
      // which would undo the language the kiosk asked for in the URL or the one
      // the guest just picked — so put it back.
      if (this.languageCode && this.$i18n.locale() !== this.languageCode) {
        await this.changeLanguage({ code: this.languageCode });
      }
    },
    async selectQrCode(qrCode) {
      if (this.loadingSession !== null) {
        return;
      }
      this.loadingSession = qrCode.session;
      this.failedSession = null;

      // Waiting for an in-flight preload keeps the two loads from interleaving:
      // fetchMenuData bails out while another menu fetch is pending, and a
      // preload that lands after our reset would put the wrong table in place.
      if (this.preloadPromise) {
        await this.preloadPromise;
      }

      if (this.preloadedSession === qrCode.session) {
        await this.$router.push({
          name: 'Menu',
          params: { session: qrCode.session },
        });
        return;
      }

      // Each customer gets a fresh session from the kiosk, so drop the cookie
      // left over from the previous one before loading this one.
      this.$store.dispatch('deleteSessionCookie');
      // Loading this table replaces whatever the preload put in the store.
      this.preloadedSession = null;

      try {
        await this.loadSession(qrCode.session);
        await this.$router.push({
          name: 'Menu',
          params: { session: qrCode.session },
        });
      } catch (e) {
        // Nothing to retry with: the session comes from the kiosk and this
        // screen cannot create a replacement. Let the guest pick again.
        console.error(e);
        this.failedSession = qrCode.session;
        this.loadingSession = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.kiosk-table-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  // The header artwork is absolutely positioned, so it needs this container
  // as its containing block — otherwise it anchors to the viewport and drifts
  // off the centred app column on wide kiosk screens.
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: $max-app-width;
  margin: 0 auto;

  &__top {
    z-index: 150;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 10px;
  }

  &__logo {
    height: 36px !important;
  }

  &__language {
    background: rgba(255, 255, 255, 0.85) !important;
    box-shadow: none !important;
    width: 36px !important;
    height: 36px !important;
  }

  &__badge {
    width: 30px;
    height: 30px;
    min-width: 30px;
    min-height: 30px;
    color: white;
  }
}
</style>
